import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Smartphone, MapPin, CheckCircle, MessageCircle, ChevronRight, Clock, Newspaper } from 'lucide-react'
import { apiUrl } from '../config/api'
import PullToRefresh from '../components/ui/PullToRefresh'

interface Mission {
  id: number; title: string; action_type: string; points: number; deadline: string | null; completed?: boolean
}

interface ChannelItem {
  id: number; name: string; slug: string; color: string | null
}

interface BlogPost {
  id: number; title: string; slug: string; excerpt: string | null
  cover_image_url: string | null; published_at: string; reading_time: number | null
  category_name: string | null
}

const digitalTypes = ['post_social', 'share_whatsapp', 'repost', 'comment', 'engage']

const actionInstructions: Record<string, string> = {
  post_social: 'Poste nas redes sociais',
  share_whatsapp: 'Compartilhe no WhatsApp',
  repost: 'Abra o post e reposte',
  comment: 'Abra o post e comente',
  engage: 'Reposte e comente no post',
  take_photo: 'Tire uma foto de comprovação',
  checkin: 'Vá ao local e faça check-in',
  sticker_car: 'Cole o adesivo no carro',
  sticker_house: 'Cole o adesivo na fachada',
  door_to_door: 'Faça visitas porta a porta',
  attend_event: 'Participe do evento',
}

export default function DashboardPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [missions, setMissions] = useState<Mission[]>([])
  const [channels, setChannels] = useState<ChannelItem[]>([])
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [userName, setUserName] = useState('')

  useEffect(() => { fetchHome() }, [])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') fetchHome(true)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  async function fetchHome(silent = false) {
    if (!silent) setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const user = localStorage.getItem('user')
      if (user) {
        try { setUserName(JSON.parse(user).name?.split(' ')[0] || '') } catch {}
      }

      const res = await fetch(apiUrl('/v1/community/my/home'), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      if (data.data) {
        setMissions(data.data.missions || [])
        setChannels(data.data.channels || [])
        localStorage.setItem('is_leader', data.data.is_leader ? 'true' : 'false')
      }

      const postsRes = await fetch(apiUrl('/v1/community/my/posts?per_page=5'), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      if (postsRes.ok) {
        const postsData = await postsRes.json()
        setPosts(postsData.data || [])
      }
    } catch {} finally { if (!silent) setLoading(false) }
  }

  const handleRefresh = async () => { await fetchHome() }
  const pendingMissions = missions.filter(m => {
    if (m.completed) return false
    if (m.deadline && new Date(m.deadline) < new Date()) return false
    return true
  })

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 animate-pulse">
        <div className="max-w-6xl mx-auto w-full space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded-lg" />
          <div className="h-36 rounded-2xl bg-gray-100" />
          <div className="h-36 rounded-2xl bg-gray-100" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-16 rounded-xl bg-gray-100" />
            <div className="h-16 rounded-xl bg-gray-100" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="h-12 px-4 items-center border-b border-border-subtle hidden lg:flex">
        <h1 className="text-base font-semibold text-content-normal">Home</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 space-y-8 max-w-6xl mx-auto w-full">

        {/* Saudação */}
        <h2 className="text-xl font-bold text-content-normal">
          Olá, {userName || 'Membro'}! 👋
        </h2>

        {/* ── O QUE FAZER HOJE ── */}
        {pendingMissions.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-bold text-content-muted uppercase tracking-widest">
                O que fazer hoje
              </h3>
              <button onClick={() => navigate(`/${slug}/missoes`)} className="text-xs text-primary font-semibold">
                Ver tudo
              </button>
            </div>
            <p className="text-xs text-content-muted mb-4">
              Complete as tarefas e ganhe pontos.
            </p>

            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 scrollbar-none">
              {pendingMissions.map(mission => {
                const isDigital = digitalTypes.includes(mission.action_type)
                const CatIcon = isDigital ? Smartphone : MapPin
                const instruction = actionInstructions[mission.action_type] || mission.action_type

                return (
                  <button
                    key={mission.id}
                    onClick={() => navigate(`/${slug}/missoes/${mission.id}`)}
                    className="snap-start shrink-0 w-[75vw] max-w-[280px] text-left rounded-2xl bg-surface-secondary border border-border-subtle overflow-hidden active:scale-[0.97] transition-transform"
                  >
                    <div className="p-4 space-y-3">
                      {/* Ícone + instrução */}
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-surface-active flex items-center justify-center shrink-0">
                          <CatIcon size={18} className="text-content-muted" />
                        </div>
                        <p className="text-[11px] text-content-muted font-medium leading-tight">{instruction}</p>
                      </div>

                      {/* Título */}
                      <p className="text-sm font-bold text-content-normal line-clamp-2 leading-snug">{mission.title}</p>

                      {/* Rodapé */}
                      <div className="flex items-center justify-between pt-2 border-t border-border-subtle">
                        <span className="text-xs text-content-muted font-medium">Ganhe {mission.points} pontos</span>
                        <span className="text-xs font-bold text-primary flex items-center gap-1">
                          Fazer <ChevronRight size={12} />
                        </span>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {/* ── CONVERSAS ── */}
        {channels.length > 0 && (
          <section>
            <h3 className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1">
              Conversas
            </h3>
            <p className="text-xs text-content-muted mb-4">
              Converse com outros membros da comunidade.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {channels.map(ch => (
                <button
                  key={ch.id}
                  onClick={() => navigate(`/${slug}/channels/${ch.slug}`)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border-subtle hover:bg-surface-hover active:scale-[0.98] transition-all text-left"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0"
                    style={{ backgroundColor: ch.color || 'var(--color-primary)' }}
                  >
                    {ch.name.substring(0, 2).toUpperCase()}
                  </div>
                  <p className="text-sm font-semibold text-content-normal flex-1 truncate">{ch.name}</p>
                  <MessageCircle size={14} className="text-content-muted shrink-0" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── NOTÍCIAS ── */}
        {posts.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-bold text-content-muted uppercase tracking-widest">
                Notícias
              </h3>
              <button onClick={() => navigate(`/${slug}/blog`)} className="text-xs text-primary font-semibold">
                Ver todas
              </button>
            </div>
            <p className="text-xs text-content-muted mb-4">
              Fique por dentro do que está acontecendo.
            </p>

            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 scrollbar-none">
              {posts.map(p => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/${slug}/blog/${p.slug}`)}
                  className="snap-start shrink-0 w-[70vw] max-w-[250px] text-left rounded-2xl bg-surface-secondary border border-border-subtle overflow-hidden active:scale-[0.97] transition-transform"
                >
                  {p.cover_image_url ? (
                    <img src={p.cover_image_url} alt={p.title} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-24 bg-primary/5 flex items-center justify-center">
                      <Newspaper size={28} className="text-primary/30" />
                    </div>
                  )}
                  <div className="p-3 space-y-1">
                    <p className="text-xs font-bold text-content-normal line-clamp-2 leading-snug">{p.title}</p>
                    <div className="flex items-center gap-2 text-[10px] text-content-muted">
                      <span>{new Date(p.published_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</span>
                      {p.reading_time && (
                        <span className="flex items-center gap-0.5"><Clock size={9} />{p.reading_time} min</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Vazio */}
        {missions.length === 0 && channels.length === 0 && posts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-sm text-content-muted">Nenhuma atividade disponível ainda.</p>
            <p className="text-xs text-content-muted mt-1">Puxe pra baixo pra atualizar.</p>
          </div>
        )}
      </div>
    </PullToRefresh>
  )
}
